<template>
  <div class="br-tab-bills">
    <section>
<KpiCards class="mb-3" :items="kpiItems" :loading="!summaries.length" :skeleton="5" />

      <!-- Ticket panel -->
      <v-card class="br-panel mb-3" flat>
        <v-card-text class="pb-2 pt-3">
          <!-- Panel header: title + status tabs -->
          <div class="d-flex align-center flex-wrap br-panel-header">
            <span class="br-accent-bar" />
            <h1 class="br-panel-title">{{ $t("ticket") }}</h1>
            <span class="br-panel-count">{{ countNote }}</span>
            <v-spacer />
            <div class="br-tabs">
              <div
                v-for="t in statusList"
                :key="t.value"
                class="br-tab"
                :class="{ 'br-tab--active': activeStatusTab === t.value }"
                @click="selectTab(t)"
              >
                {{ t.text }}
                <span class="br-tab-count">{{ statusCount(t.value) }}</span>
              </div>
            </div>
          </div>

          <!-- Filter row -->
          <div class="d-flex align-center flex-wrap br-filter-row mt-3">
            <TextSearch
              v-model="search"
              class="br-search"
              @search="filterData()"
              @onClear="
                search = '';
                filterData();
              "
            />
            <div class="d-flex align-center br-date-range">
              <DatePicker
                :dense="true"
                :placeholder="false"
                label="start_date"
                v-model="start_date"
                hide-details
                style="max-width: 152px"
              />
              <span class="br-date-arrow mx-1">→</span>
              <DatePicker
                :dense="true"
                :placeholder="false"
                label="end_date"
                v-model="end_date"
                hide-details
                style="max-width: 152px"
              />
            </div>
            <TextSelect
              class="br-select"
              :dense="true"
              :placeholder="false"
              :items="paymentList"
              label="payment_method"
              itemValue="value"
              text="name"
              :returnObject="false"
              v-model="payment_method"
              hide-details
            />
            <TextSelect
              v-if="isAdvancedReportEnabled"
              class="br-select"
              :dense="true"
              :placeholder="false"
              :items="usersList"
              label="seller"
              itemValue="cashier_id"
              text="first_name"
              :returnObject="false"
              v-model="cashier_id"
              hide-details
            />
            <TextSelect
              v-if="isTicketVerificationEnabled"
              class="br-select br-select--verification"
              :dense="true"
              :placeholder="false"
              :items="verificationStatusList"
              label="verification_status"
              itemValue="value"
              text="text"
              :returnObject="false"
              v-model="verification_status"
              hide-details
            />
            <span v-if="isFiltered" class="br-clear-link" @click="resetFilters">
              {{ $t("clear_filters") }}
            </span>
            <v-spacer />
            <v-btn
              v-if="isAdvancedReportEnabled"
              small
              text
              color="primary"
              @click="toggleAdvancedFilter"
              class="text-capitalize"
            >
              <v-icon left small>fal fa-ellipsis-v</v-icon>
              {{ $t("more_filter") }}
            </v-btn>
            <div class="br-export-btn" @click="handleExcelExport">
              <v-icon size="13" class="mr-1">fal fa-file-export</v-icon>
              {{ $t("export") }}
            </div>
          </div>

          <!-- Advanced Filter Section -->
          <v-expand-transition>
            <div v-show="showAdvancedFilter">
              <v-divider class="my-3"></v-divider>
              <AdvancedReportFilter
                :logisticsList="logisticsList"
                :customersList="customersList"
                :usersList="usersList"
                :selectedTicketsCount="selectedTickets.length"
                :initialFilterData="advancedFilterData"
                :user="user"
                @filter-change="handleAdvancedFilterChange"
                @batch-print="handleBatchPrint"
                @excel-export="handleExcelExport"
              />
            </div>
          </v-expand-transition>

          <!-- Bulk selection bar -->
          <div
            v-if="hasSelection"
            class="d-flex align-center br-selection-bar mt-3"
          >
            <span class="br-selection-label">
              {{ selectedTickets.length }} {{ $t("selected_tickets") }}
            </span>
            <span class="br-selection-total">
              {{ selectedTotal | NumberFormat }} ₭
            </span>
            <v-spacer />
            <span class="br-selection-action" @click="handleBatchPrint">
              {{ $t("batch_print") }}
            </span>
            <span class="br-selection-action" @click="clearAllSelections">
              {{ $t("clear_all") }}
            </span>
          </div>

          <!-- Table -->
          <div class="br-table-scroll mt-3">
            <div class="br-table-inner">
              <div class="br-row br-head-row">
                <span class="br-cell br-cell-check">
                  <span
                    v-if="isAdvancedReportEnabled"
                    class="br-checkbox"
                    :class="{ 'br-checkbox--on': allSelected }"
                    @click="toggleSelectAll"
                  >
                    <v-icon v-if="allSelected" size="9" color="white"
                      >fal fa-check</v-icon
                    >
                  </span>
                </span>
                <span
                  v-for="col in columns"
                  :key="col.key"
                  class="br-cell"
                  :class="[
                    'br-col-' + col.key,
                    { 'br-cell--sortable': !col.noSort },
                  ]"
                  @click="!col.noSort && toggleSort(col.key)"
                >
                  <span>{{ col.key === "seq" ? "#" : $t(col.label) }}</span>
                  <span v-if="sortKey === col.key" class="br-sort-caret">{{
                    sortDir === 1 ? "↑" : "↓"
                  }}</span>
                </span>
              </div>

              <template v-if="billReportsData.length">
                <div
                  v-for="(r, i) in sortedBillData"
                  :key="r.id"
                  class="br-row br-body-row"
                  :class="{ 'br-row--selected': isSelected(r) }"
                  @click="viewItem(r)"
                >
                  <span class="br-cell br-cell-check" @click.stop>
                    <span
                      v-if="isAdvancedReportEnabled"
                      class="br-checkbox"
                      :class="{ 'br-checkbox--on': isSelected(r) }"
                      @click="toggleSelect(r)"
                    >
                      <v-icon v-if="isSelected(r)" size="9" color="white"
                        >fal fa-check</v-icon
                      >
                    </span>
                  </span>
                  <span class="br-cell br-col-seq">{{ i + 1 }}</span>
                  <div class="br-cell br-col-code br-stack">
                    <span class="br-code">{{ r.code }}</span>
                    <span class="br-subtext">#{{ r.id }}</span>
                  </div>
                  <div class="br-cell br-col-user_name br-seller">
                    <span class="br-avatar">{{ initials(r.user_name) }}</span>
                    <div class="br-stack">
                      <span class="br-seller-name">{{ r.user_name }}</span>
                      <span
                        class="br-customer"
                        :class="{ 'br-customer--empty': !r.customer_name }"
                        >{{ r.customer_name || "—" }}</span
                      >
                    </div>
                  </div>
                  <div class="br-cell br-col-status br-tags">
                    <span class="br-tag" :class="tagClass(r.status)">{{
                      $t(r.status)
                    }}</span>
                    <BillEditBadge
                      v-if="
                        (r.cancel_summary ||
                          r.add_summary ||
                          r.pre_checkout_cancel_summary) &&
                        r.status !== 'cancel'
                      "
                      :cancel-summary="r.cancel_summary"
                      :add-summary="r.add_summary"
                      :pre-checkout-summary="r.pre_checkout_cancel_summary"
                      :current-total="r.grand_total"
                    />
                    <span
                      v-if="isTicketVerificationEnabled"
                      class="br-tag"
                      :class="verifyTagClass(r.verification_status)"
                      >{{ $t(r.verification_status || "pending") }}</span
                    >
                  </div>
                  <span class="br-cell br-col-type">
                    <span class="br-tag br-tag--neutral">{{ $t(r.type) }}</span>
                  </span>
                  <span class="br-cell br-col-total_qty br-num">{{
                    r.total_qty | ToNumber
                  }}</span>
                  <span class="br-cell br-col-total_price_buy br-num">{{
                    formatCost(r.total_price_buy)
                  }}</span>
                  <div
                    class="br-cell br-col-total_profit br-num br-stack br-stack-end"
                  >
                    <span class="br-profit">{{
                      formatProfit(r.total_profit)
                    }}</span>
                    <span v-if="marginNote(r)" class="br-subtext">{{
                      marginNote(r)
                    }}</span>
                  </div>
                  <div
                    class="br-cell br-col-grand_total br-num br-total-strong"
                  >
                    <span>{{ r.grand_total | NumberFormat }}</span>
                    <div
                      v-if="
                        r.cancel_summary &&
                        r.status !== 'cancel' &&
                        Number(r.cancel_summary.original_total) !==
                          Number(r.grand_total)
                      "
                      class="text-caption text--secondary text-no-wrap"
                    >
                      {{ $t("original_total") }}
                      <span class="text-decoration-line-through">
                        {{ r.cancel_summary.original_total | NumberFormat }}
                      </span>
                    </div>
                  </div>
                  <div
                    class="br-cell br-col-created_at br-num br-stack br-stack-end"
                  >
                    <span>{{ r.created_at | momentTime }}</span>
                    <span class="br-subtext">{{
                      r.created_at | created_at
                    }}</span>
                  </div>
                  <span class="br-cell br-col-manage br-actions" @click.stop>
                    <span
                      class="br-action-icon"
                      :title="$t('description')"
                      @click="viewItem(r)"
                    >
                      <v-icon size="13">fal fa-eye</v-icon>
                    </span>
                    <span
                      v-if="r.status !== 'WAITING_PAYMENT'"
                      class="br-action-icon"
                      :title="$t('print')"
                      @click="editItem(r)"
                    >
                      <v-icon size="13">fal fa-print</v-icon>
                    </span>
                    <span
                      v-if="
                        r.status === 'WAITING_PAYMENT' || r.status === 'cancel'
                      "
                      class="br-action-icon"
                      :title="$t('delete')"
                      @click="deleteItem(r)"
                    >
                      <v-icon size="13">fal fa-trash</v-icon>
                    </span>
                    <span
                      v-else
                      class="br-action-icon"
                      :title="$t('cancel_items_refund')"
                      @click="deleteItem(r)"
                    >
                      <v-icon size="13">fal fa-undo</v-icon>
                    </span>
                  </span>
                </div>
              </template>

              <div v-else class="br-empty">
                <v-icon size="26" color="grey lighten-1">fal fa-receipt</v-icon>
                <span class="br-empty-text">{{ $t("no_data") }}</span>
                <span
                  v-if="isFiltered"
                  class="br-clear-link"
                  @click="resetFilters"
                >
                  {{ $t("clear_filters") }}
                </span>
              </div>

              <div v-if="billReportsData.length" class="br-row br-foot-row">
                <span></span>
                <span class="br-foot-label" style="grid-column: 2 / 8">{{
                  $t("total")
                }}</span>
                <span class="br-num">{{ sumQty | ToNumber }}</span>
                <span class="br-num">{{ sumCost }}</span>
                <span class="br-num br-profit">{{ sumProfit }}</span>
                <span class="br-num br-total-strong">{{
                  sumTotal | NumberFormat
                }}</span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>

          <!-- Pagination footer -->
          <div class="d-flex align-center br-pagination mt-2 flex-wrap">
            <span class="br-page-note">{{ pageNote }}</span>
            <v-spacer />
            <span class="br-page-note mr-2">{{ $t("rows_per_page") }}</span>
            <select
              class="br-native-select"
              :value="per_page"
              @change="onPerPageChange($event.target.value)"
            >
              <option v-for="n in rowsPerPageItems" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
            <div class="br-page-nav ml-3">
              <span
                class="br-page-btn"
                :class="{ 'br-page-btn--disabled': !billReports.prev_page_url }"
                @click="goToPage(-1)"
              >
                <v-icon size="14">fal fa-chevron-left</v-icon>
              </span>
              <span class="br-page-current">{{
                billReports.current_page
              }}</span>
              <span
                class="br-page-btn"
                :class="{ 'br-page-btn--disabled': !billReports.next_page_url }"
                @click="goToPage(1)"
              >
                <v-icon size="14">fal fa-chevron-right</v-icon>
              </span>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <app-custom-dialog
        width="900px"
        v-model="dialog"
        :footer="false"
        title="description"
      >
        <div slot="activator">
          <AppDetail :item="form" @ticket-verified="handleTicketVerified" />
        </div>
      </app-custom-dialog>
      <!-- bill print -->
      <app-bill v-show="print" :item="form" />

      <!-- batch print container -->
      <div id="batchPrintContainer" v-show="false" class="batch-print-wrapper">
        <div
          v-for="(bill, index) in batchPrintBills"
          :key="`batch-${bill.id}-${index}`"
          class="batch-bill-page"
          :class="{
            'first-page': index === 0,
            'last-page': index === batchPrintBills.length - 1,
          }"
        >
          <div class="receipt-wrapper">
            <app-bill :item="bill" />
          </div>
        </div>
      </div>

      <!-- Cancel items or the whole bill -->
      <CancelTicketDialog
        v-model="dialogAdd"
        :ticket="cancelTicket"
        @cancelled="onTicketCancelled"
        @print="printCancelledTicket"
      />
      <!-- Batch Print Dialog -->
      <app-custom-dialog
        v-model="batchPrintDialog"
        :footer="true"
        title="batch_print_receipts"
        @submit="executeBatchPrint"
      >
        <div slot="activator">
          <v-card-text>
            <v-row class="mb-3">
              <v-col cols="6">
                <v-chip color="primary" outlined>
                  {{ $t("selected_tickets") }}: {{ selectedTickets.length }}
                </v-chip>
              </v-col>
              <v-col cols="6" class="text-right">
                <v-btn
                  small
                  color="error"
                  @click="clearAllSelections"
                  :disabled="selectedTickets.length === 0"
                >
                  <v-icon left small>fal fa-times</v-icon>
                  {{ $t("clear_all") }}
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              :headers="batchPrintHeaders"
              :items="selectedTickets"
              :items-per-page="10"
              class="elevation-1"
              :loading="overlay"
            >
              <template v-slot:[`item.total_price`]="{ item }">
                {{ item.total_price | NumberFormat }}
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn small color="error" @click="removeFromBatch(item)">
                  <v-icon left small>fal fa-times</v-icon>
                  {{ $t("remove") }}
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </div>
      </app-custom-dialog>

      <!-- Ticket Verification Dialog -->
      <app-custom-dialog
        v-model="verificationDialog"
        :footer="true"
        title="verify_ticket"
        @submit="verifyTicket"
      >
        <div slot="activator">
          <v-card-text>
            <v-text-field
              v-model="verificationForm.ticket_code"
              :label="$t('ticket_code')"
              readonly
              outlined
            />
            <v-select
              v-model="verificationForm.verification_status"
              :items="verificationStatusList"
              :label="$t('verification_status')"
              item-value="value"
              item-text="text"
              outlined
            />
            <v-textarea
              v-model="verificationForm.reason"
              :label="$t('reason')"
              :placeholder="$t('enter_reason_if_rejected')"
              outlined
              :disabled="verificationForm.verification_status === 'approved'"
            />
          </v-card-text>
        </div>
      </app-custom-dialog>

      <!-- AppLoading -->
      <app-loading :overlay="overlay" />
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import AppDetail from "@/components/products/billReport";
import { myMixin } from "@/mixin/main";
import AppBill from "@/components/print/bill";
import { returnProduct } from "@/validations/shop";
import TextArea from "@/components/global/TextArea";
import TextSelect from "@/components/global/TextSelect";
import AdvancedReportFilter from "@/components/reports/AdvancedReportFilter";
import CancelTicketDialog from "@/components/reports/CancelTicketDialog";
import BillEditBadge from "@/components/reports/BillEditBadge";
import { toast, error, confirm } from "@/controllers/alert";
import { ticketStatus, refundList } from "@/utils/mock";
import { hasPermission } from "@/controllers/auth";
import { formatBillQueue } from "@/utils/billQueue";

// get today date
const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
  .toISOString()
  .substr(0, 10);

// get tomorrow date
const tomorrow = new Date(
  Date.now() - new Date().getTimezoneOffset() * 60000 + 86400000,
)
  .toISOString()
  .substr(0, 10);

// Column order/labels for the ticket table (kept out of `data` since it's static)
const COLUMNS = [
  { key: "seq", label: "#", noSort: true },
  { key: "code", label: "code" },
  { key: "user_name", label: "seller" },
  { key: "status", label: "status" },
  { key: "type", label: "payment_method" },
  { key: "total_qty", label: "quantity" },
  { key: "total_price_buy", label: "cost" },
  { key: "total_profit", label: "profit" },
  { key: "grand_total", label: "grand_total" },
  { key: "created_at", label: "date" },
  { key: "manage", label: "manage", noSort: true },
];

export default {
  mixins: [returnProduct, myMixin],

  props: {
    startDate: {
      type: String,
      default: "",
    },
    endDate: {
      type: String,
      default: "",
    },
    initialStatusTab: {
      type: String,
      default: "all",
    },
  },

  data: () => ({
    search: "",
    dialog: false,
    print: false,
    summaries: [],
    form: {},
    activeStatusTab: "all",
    status: {
      text: "all",
      value: "all",
    },
    start_date: today,
    end_date: tomorrow,
    start_time: "",
    end_time: "",
    logistics_id: "all",
    cashier_id: "all",
    customer_id: "all",
    verification_status: "all",
    logisticsList: [],
    usersList: [],
    customersList: [],
    selectedTickets: [],
    batchPrintDialog: false,
    verificationDialog: false,
    menu: false,
    menu2: false,
    dialogAdd: false,
    showAdvancedFilter: false,
    batchPrintBills: [],
    payment_method: "all",
    hasCancelledItems: false,
    cancelTicket: null,
    isInitialLoad: true, // Flag to prevent watchers from triggering on initial load
    filterDebounceTimer: null, // Timer for debouncing filter calls
    sortKey: "created_at",
    sortDir: -1,
    columns: COLUMNS,

    body: {
      return_type: "not_match",
      ticket_id: null,
      remark: "",
    },

    verificationForm: {
      ticket_code: "",
      verification_status: "approved",
      reason: "",
      ticket_id: null,
    },

    batchPrintHeaders: [
      { text: "Code", value: "code" },
      { text: "Customer", value: "customer_name" },
      { text: "Cashier", value: "user_name" },
      { text: "Total", value: "total_price" },
      { text: "Status", value: "status" },
      { text: "Actions", value: "actions", sortable: false },
    ],
  }),
  async created() {
    if (this.startDate) {
      this.start_date = this.startDate;
    }
    if (this.endDate) {
      this.end_date = this.endDate;
    }
    if (this.initialStatusTab === "cancelled_items") {
      this.activeStatusTab = "cancelled_items";
      this.hasCancelledItems = true;
      this.status = { text: this.$t("all"), value: "all" };
    }
    this.SET_OVERLAY(true);
    try {
      await Promise.all([
        this.fetchSummary(),
        this.fetchItem(),
        this.fetchLogistics(),
        this.fetchUsers(),
        this.fetchCustomers(),
      ]);
    } finally {
      this.SET_OVERLAY(false);
      // Mark initial load as complete after a short delay
      this.$nextTick(() => {
        setTimeout(() => {
          this.isInitialLoad = false;
        }, 100);
      });
    }
  },
  components: {
    TextSelect,
    AppDetail,
    TextArea,
    AppBill,
    AdvancedReportFilter,
    CancelTicketDialog,
    BillEditBadge,
  },
  computed: {
    kpiItems() {
      return this.summaries.map((item) => ({
        key: item.title,
        title: item.title,
        icon: item.icon,
        value: this.formatSummaryValue(item),
        note: item.description ? this.$t(item.description) : "",
      }));
    },
    ...mapGetters({
      overlay: "index/overlay",
      billReports: "index/billReports",
      user: "auth/user",
      per_page: "index/per_page",
      rowsPerPageItems: "index/rowsPerPageItems",
    }),
    categories() {
      return refundList(this);
    },
    statusList() {
      return [
        {
          text: this.$t("all"),
          value: "all",
        },
        {
          text: this.$t("done"),
          value: "done",
        },
        {
          text: this.$t("cancel"),
          value: "cancel",
        },
        {
          text: this.$t("cancelled_items"),
          value: "cancelled_items",
        },
      ];
    },
    // Safe access to bill reports data
    billReportsData() {
      return this.billReports && this.billReports.data
        ? this.billReports.data
        : [];
    },
    // Rows for the current page, sorted client-side by the active column
    sortedBillData() {
      const list = [...this.billReportsData];
      if (!this.sortKey) return list;
      const key = this.sortKey;
      const dir = this.sortDir;
      return list.sort((a, b) => {
        let x = a[key];
        let y = b[key];
        if (typeof x === "string") x = x.toLowerCase();
        if (typeof y === "string") y = y.toLowerCase();
        if (x == null) x = "";
        if (y == null) y = "";
        if (x < y) return -1 * dir;
        if (x > y) return 1 * dir;
        return 0;
      });
    },
    // Check if advanced filter should be available based on store setting
    isAdvancedReportEnabled() {
      return (
        this.user && this.user.stores && this.user.stores.advanced_report === 1
      );
    },
    // Whether tickets on this store go through the verification workflow
    isTicketVerificationEnabled() {
      return (
        this.user &&
        this.user.stores &&
        this.user.stores.ticket_verification === 1
      );
    },

    // Verification status list for filter
    verificationStatusList() {
      return [
        { value: "all", text: this.$t("all") },
        { value: "approved", text: this.$t("approved") },
        { value: "rejected", text: this.$t("rejected") },
      ];
    },

    // Advanced filter data for the component
    advancedFilterData() {
      return {
        logistics_id: this.logistics_id,
        customer_id: this.customer_id,
        cashier_id: this.cashier_id,
        start_time: this.start_time,
        end_time: this.end_time,
        verification_status: this.verification_status,
      };
    },

    paymentList() {
      return [
        {
          name: this.$t("all"),
          icon: "fal fa-money-bill",
          value: "all",
        },
        {
          name: this.$t("cash"),
          icon: "fal fa-money-bill",
          value: "cash",
        },
        {
          name: this.$t("qr_code"),
          icon: "fal fa-qrcode",
          value: "transfer",
        },
        {
          name: this.$t("cash_with_transfer"),
          icon: "fal fa-money-bill-wave",
          value: "cash_transfer",
        },
        {
          name: this.$t("cod"),
          icon: "fal fa-truck",
          value: "cod",
        },
      ];
    },

    // Check if current user can delete/cancel tickets
    canDeleteTicket() {
      return hasPermission("can_delete_ticket");
    },

    countNote() {
      const total = this.billReports && this.billReports.total;
      return total != null ? `${total} ${this.$t("ticket")}` : "";
    },

    isFiltered() {
      return !!(
        this.search.trim() ||
        this.activeStatusTab !== "all" ||
        this.payment_method !== "all" ||
        this.cashier_id !== "all" ||
        this.verification_status !== "all"
      );
    },

    hasSelection() {
      return this.selectedTickets.length > 0;
    },
    selectedTotal() {
      return this.selectedTickets.reduce(
        (sum, t) => sum + (Number(t.grand_total) || 0),
        0,
      );
    },
    allSelected() {
      return (
        this.sortedBillData.length > 0 &&
        this.sortedBillData.every((r) => this.isSelected(r))
      );
    },

    sumQty() {
      return this.billReportsData.reduce(
        (n, b) => n + (Number(b.total_qty) || 0),
        0,
      );
    },
    sumCost() {
      return this.formatCost(
        this.billReportsData.reduce(
          (n, b) => n + (Number(b.total_price_buy) || 0),
          0,
        ),
      );
    },
    sumProfit() {
      return this.formatProfit(
        this.billReportsData.reduce(
          (n, b) => n + (Number(b.total_profit) || 0),
          0,
        ),
      );
    },
    sumTotal() {
      return this.billReportsData.reduce(
        (n, b) => n + (Number(b.grand_total) || 0),
        0,
      );
    },

    pageNote() {
      if (!this.billReports || !this.billReports.total) {
        return this.$t("no_data");
      }
      return `${this.billReports.from} – ${this.billReports.to} / ${this.billReports.total}`;
    },
  },
  mounted() {
    // Initialize advanced filter state from localStorage, but only if advanced reports are enabled
    if (this.isAdvancedReportEnabled) {
      this.showAdvancedFilter = this.getAdvancedFilterState();
    } else {
      this.showAdvancedFilter = false;
    }
  },
  beforeDestroy() {
    // Clear debounce timer on component destroy
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = null;
    }
  },
  watch: {
    showAdvancedFilter(newVal) {
      // Save to localStorage whenever the state changes
      this.saveAdvancedFilterState();
    },
    // Watch status filter (deep watch since it's an object)
    status: {
      handler(val) {
        if (!this.isInitialLoad) {
          this.debouncedFilter();
        }
      },
      deep: true,
    },
    // Watch payment method filter
    payment_method(val) {
      if (!this.isInitialLoad) {
        this.debouncedFilter();
      }
    },
    hasCancelledItems() {
      if (!this.isInitialLoad) {
        this.debouncedFilter();
      }
    },
    initialStatusTab(newVal) {
      if (newVal === "cancelled_items") {
        this.selectTab({
          text: this.$t("cancelled_items"),
          value: "cancelled_items",
        });
      } else {
        this.selectTab({ text: this.$t(newVal), value: newVal });
      }
    },
    startDate(newVal) {
      if (newVal && newVal !== this.start_date) {
        this.start_date = newVal;
      }
    },
    endDate(newVal) {
      if (newVal && newVal !== this.end_date) {
        this.end_date = newVal;
      }
    },
    // Watch start date filter
    start_date(val) {
      this.$emit("date-change", { startDate: val, endDate: this.end_date });
      if (!this.isInitialLoad) {
        this.debouncedFilter();
      }
    },
    // Watch end date filter
    end_date(val) {
      this.$emit("date-change", { startDate: this.start_date, endDate: val });
      if (!this.isInitialLoad) {
        this.debouncedFilter();
      }
    },
    // Watch logistics filter
    logistics_id(val) {
      if (!this.isInitialLoad) {
        this.debouncedFilter();
      }
    },
    // Watch customer filter
    customer_id(val) {
      if (!this.isInitialLoad) {
        this.debouncedFilter();
      }
    },
    // Watch cashier filter
    cashier_id(val) {
      if (!this.isInitialLoad) {
        this.debouncedFilter();
      }
    },
    // Watch start time filter
    start_time(val) {
      if (!this.isInitialLoad) {
        this.debouncedFilter();
      }
    },
    // Watch end time filter
    end_time(val) {
      if (!this.isInitialLoad) {
        this.debouncedFilter();
      }
    },
    // Watch verification status filter
    verification_status(val) {
      if (!this.isInitialLoad) {
        this.debouncedFilter();
      }
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),
    ...mapMutations({
      SET_OVERLAY: "index/SET_OVERLAY",
      SET_PER_PAGE: "index/SET_PER_PAGE",
    }),
    formatBillQueue,
    /**
     * Map frontend payment method value to backend/ticket type.
     * Tickets store type as "cash_transfer", UI uses "cash_with_transfer".
     */
    paymentMethodForApi() {
      if (!this.payment_method || this.payment_method === "all")
        return this.payment_method;
      return this.payment_method === "cash_with_transfer"
        ? "cash_transfer"
        : this.payment_method;
    },
    async fetchSummary() {
      try {
        let url = `/get_summary_bill?start_date=${this.start_date}&end_date=${this.end_date}`;

        // Add status filter
        if (this.status && this.status.value && this.status.value !== "all") {
          url += `&status=${this.status.value}`;
        }

        // Add logistics filter
        if (
          this.logistics_id &&
          this.logistics_id !== "all" &&
          this.logistics_id !== null &&
          this.logistics_id !== undefined
        ) {
          url += `&logistics_id=${this.logistics_id}`;
        }

        // Add cashier filter
        if (
          this.cashier_id &&
          this.cashier_id !== "all" &&
          this.cashier_id !== null &&
          this.cashier_id !== undefined
        ) {
          url += `&cashier_id=${this.cashier_id}`;
        }

        // Add time filters
        if (this.start_time) {
          url += `&start_time=${this.start_time}`;
        }
        if (this.end_time) {
          url += `&end_time=${this.end_time}`;
        }

        // Add verification status filter
        if (this.verification_status && this.verification_status !== "all") {
          url += `&verification_status=${this.verification_status}`;
        }

        // Add payment method filter (map cash_with_transfer → cash_transfer for API)
        const paymentMethodParam = this.paymentMethodForApi();
        if (paymentMethodParam && paymentMethodParam !== "all") {
          url += `&payment_method=${paymentMethodParam}`;
        }

        // Add search filter
        if (this.search && this.search.trim() !== "") {
          url += `&q=${encodeURIComponent(this.search.trim())}`;
        }

        // Add customer filter
        if (
          this.customer_id &&
          this.customer_id !== "all" &&
          this.customer_id !== null &&
          this.customer_id !== undefined
        ) {
          url += `&customer_id=${this.customer_id}`;
        }

        if (this.hasCancelledItems) {
          url += `&has_cancelled_items=1`;
        }

        const { data } = await this.$http.get(url);
        if (data.status) {
          this.summaries = data.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async filterData() {
      this.SET_OVERLAY(true);
      try {
        await Help.getDifferentMonth(this, this.start_date, this.end_date);
        await Promise.all([this.fetchItem(), this.fetchSummary()]);
      } catch (err) {
        console.log("error", err.message);
        error(this, err.message);
        return;
      } finally {
        this.SET_OVERLAY(false);
      }
    },
    async searchData() {
      this.SET_OVERLAY(true);
      try {
        await this.fetchItem();
      } finally {
        this.SET_OVERLAY(false);
      }
    },
    async fetchItem() {
      let url = `bill_report?status=${this.status.value}&start_date=${this.start_date}&end_date=${this.end_date}&q=${this.search}`;

      // Add new filter parameters (skip "all" — same as fetchSummary)
      if (
        this.logistics_id &&
        this.logistics_id !== "all" &&
        this.logistics_id !== null &&
        this.logistics_id !== undefined
      ) {
        url += `&logistics_id=${this.logistics_id}`;
      }
      if (
        this.cashier_id &&
        this.cashier_id !== "all" &&
        this.cashier_id !== null &&
        this.cashier_id !== undefined
      ) {
        url += `&cashier_id=${this.cashier_id}`;
      }
      if (
        this.customer_id &&
        this.customer_id !== null &&
        this.customer_id !== undefined &&
        this.customer_id !== "all"
      ) {
        url += `&customer_id=${this.customer_id}`;
      }
      if (this.start_time) {
        url += `&start_time=${this.start_time}`;
      }
      if (this.end_time) {
        url += `&end_time=${this.end_time}`;
      }
      if (this.verification_status && this.verification_status !== "all") {
        url += `&verification_status=${this.verification_status}`;
      }
      const paymentMethodParam = this.paymentMethodForApi();
      if (paymentMethodParam && paymentMethodParam !== "all") {
        url += `&payment_method=${paymentMethodParam}`;
      }
      if (this.hasCancelledItems) {
        url += `&has_cancelled_items=1`;
      }
      const body = {
        that: this,
        url: url,
        commit: "SET_BILL_REPORT",
      };
      await this.fetch(body);
    },
    async fetchLogistics() {
      try {
        const { data } = await this.$http.get("/get_logistics_for_filter");
        if (data.status) {
          // Add "All" option at the beginning of the list
          this.logisticsList = [
            { id: "all", name: this.$t("all"), logistics_id: "all" },
            ...data.data,
          ];
        }
      } catch (error) {
        console.log("Error fetching logistics:", error);
      }
    },
    async fetchUsers() {
      try {
        const { data } = await this.$http.get("/get_users_for_filter");
        if (data.status) {
          // Add "All" option at the beginning of the list
          this.usersList = [
            { id: "all", first_name: this.$t("all"), cashier_id: "all" },
            ...data.data,
          ];
        }
      } catch (error) {
        console.log("Error fetching users:", error);
      }
    },

    async fetchCustomers() {
      try {
        const { data } = await this.$http.get("/get_customers_for_filter");
        if (data.status) {
          this.customersList = [
            { id: "all", first_name: this.$t("all"), customer_id: "all" },
            ...data.data,
          ];
        }
      } catch (error) {
        console.log("Error fetching customers:", error);
      }
    },

    async deleteItem({ id, status }) {
      // Step 1: Already cancelled or waiting payment → hard delete, requires can_delete_ticket
      if (status === "WAITING_PAYMENT" || status === "cancel") {
        // confirm dialog to delete
        const isConfirm = await confirm({
          props: this,
          title: "are_you_sure",
          text: "want_to_delete_waiting_payment",
        });
        if (!isConfirm.isConfirmed) return;
        this.SET_OVERLAY(true);
        try {
          const { data } = await this.$http.delete(`delete_txn/${id}`);
          if (data.status) {
            await Promise.all([this.fetchItem(), this.fetchSummary()]);
            await toast(this, "success");
          }
        } catch (err) {
          console.log("error", err);
          error(this, err.message);
          return;
        } finally {
          this.SET_OVERLAY(false);
        }
        return;
      }

      // For other statuses, show the cancel dialog
      const { data } = await this.$http.get(`bill_detail/${id}`);
      if (data.status) {
        this.cancelTicket = data.data;
        this.dialogAdd = true;
      }
    },
    async onTicketCancelled() {
      this.cancelTicket = null;
      await Promise.all([this.fetchItem(), this.fetchSummary()]);
    },
    printCancelledTicket(ticket) {
      if (!ticket) return;
      this.form = ticket;
      this.$nextTick(() => {
        setTimeout(() => {
          this.$htmlToPaper("printBill");
        }, 500);
      });
    },
    async viewItem({ id }) {
      const { data } = await this.$http.get(`bill_detail/${id}`);
      this.form = data.data;
      this.dialog = true;
    },

    async editItem({ id }) {
      const { data } = await this.$http.get(`bill_detail/${id}`);
      this.form = data.data;

      setTimeout(() => {
        setTimeout(() => {
          this.$htmlToPaper("printBill");
        }, 500);
      }, 500);
    },
    async submitForm() {
      this.SET_OVERLAY(true);
      try {
        this.$v.body.$touch();
        if (!this.$v.body.$invalid) {
          const { data } = await this.$http.post("cancel_ticket", this.body);
          if (data.status) {
            this.dialogAdd = !this.dialogAdd;
            await Help.clearForm(this.body, this);
            await toast(this, "cancel_ticket_success");
            await this.fetchItem();
          }
        }
      } catch (error) {
        console.log("error", error);
      } finally {
        this.SET_OVERLAY(false);
      }
    },

    removeFromBatch(ticket) {
      this.selectedTickets = this.selectedTickets.filter(
        (t) => t.id !== ticket.id,
      );
    },
    async executeBatchPrint() {
      try {
        this.SET_OVERLAY(true);
        this.batchPrintBills = []; // Clear previous batch

        // Fetch all bill details
        const billPromises = this.selectedTickets.map((ticket) =>
          this.$http.get(`bill_detail/${ticket.id}`),
        );

        const billResponses = await Promise.all(billPromises);

        // Collect all successful bill data
        this.batchPrintBills = billResponses
          .filter((response) => response.data.status)
          .map((response) => response.data.data);

        if (this.batchPrintBills.length === 0) {
          throw new Error("No valid bills found for printing");
        }

        // Wait for DOM to update with all bills
        await this.$nextTick();

        // Force a reflow to ensure CSS is applied
        const container = document.getElementById("batchPrintContainer");
        if (container) {
          container.offsetHeight; // Force reflow
        }

        // Print all bills at once with longer delay to ensure CSS is loaded
        setTimeout(() => {
          // Rely on @media print CSS to reveal the container in the print context
          this.$htmlToPaper("batchPrintContainer", {
            margin: 0,
            filename: `batch_receipts_${new Date().getTime()}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          });

          // Ensure it stays hidden in the main page after triggering print
          const printContainer = document.getElementById("batchPrintContainer");
          if (printContainer) {
            printContainer.style.display = "none";
            printContainer.style.visibility = "hidden";
          }
        }, 1500);

        this.batchPrintDialog = false;
        this.selectedTickets = [];
        await toast(this, "batch_print_completed");
      } catch (error) {
        console.log("Error in batch print:", error);
        error(this, "batch_print_failed");
      } finally {
        this.SET_OVERLAY(false);
      }
    },

    // Ticket Verification Methods
    async verifyTicketDialog(ticket) {
      this.verificationForm.ticket_code = ticket.code;
      this.verificationForm.ticket_id = ticket.id;
      this.verificationForm.verification_status = "approved";
      this.verificationForm.reason = "";
      this.verificationDialog = true;
    },
    async verifyTicket() {
      this.SET_OVERLAY(true);
      try {
        const { data } = await this.$http.post("verify_ticket", {
          ticket_id: this.verificationForm.ticket_id,
          verification_status: this.verificationForm.verification_status,
          reason: this.verificationForm.reason,
        });

        if (data.status) {
          await toast(this, "ticket_verification_success");
          this.verificationDialog = false;
          await this.fetchItem();
        }
      } catch (error) {
        console.log("Error verifying ticket:", error);
        error(this, "ticket_verification_failed");
      } finally {
        this.SET_OVERLAY(false);
      }
    },

    // Clear all selections
    clearAllSelections() {
      this.selectedTickets = [];
      toast(this, "selections_cleared");
    },

    // Toggle advanced filter visibility
    toggleAdvancedFilter() {
      // Only allow toggling if advanced reports are enabled
      if (this.isAdvancedReportEnabled) {
        this.showAdvancedFilter = !this.showAdvancedFilter;
        this.saveAdvancedFilterState();
      }
    },

    // Handle ticket verification event from child component
    handleTicketVerified(verificationData) {
      console.log("Ticket verified:", verificationData);
      // Refresh the bill reports to show updated verification status
      Promise.all([this.fetchItem(), this.fetchSummary()]);
    },

    // Get verification status color for chips
    getVerificationStatusColor(status) {
      switch (status) {
        case "approved":
          return "success";
        case "rejected":
          return "error";
        default:
          return "warning";
      }
    },

    // Handle advanced filter changes from child component
    handleAdvancedFilterChange(filterData) {
      // Temporarily disable watchers to prevent duplicate calls
      const wasInitialLoad = this.isInitialLoad;
      this.isInitialLoad = true;

      this.logistics_id = filterData.logistics_id;
      this.customer_id = filterData.customer_id;
      this.cashier_id = filterData.cashier_id;
      this.start_time = filterData.start_time;
      this.end_time = filterData.end_time;
      this.verification_status = filterData.verification_status;

      // Restore watcher state
      this.$nextTick(() => {
        this.isInitialLoad = wasInitialLoad;
      });

      // Apply the filter (filterData already handles the API call)
      this.filterData();
    },

    // Reset the main toolbar filters (search / status / payment method) back to defaults
    resetFilters() {
      const wasInitialLoad = this.isInitialLoad;
      this.isInitialLoad = true;

      this.search = "";
      this.activeStatusTab = "all";
      this.status = { text: this.$t("all"), value: "all" };
      this.payment_method = "all";
      this.cashier_id = "all";
      this.verification_status = "all";
      this.hasCancelledItems = false;

      this.$nextTick(() => {
        this.isInitialLoad = wasInitialLoad;
      });

      this.filterData();
    },

    // Debounced filter method to avoid too many API calls
    async debouncedFilter() {
      // Clear existing timer
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer);
      }

      // Set new timer
      this.filterDebounceTimer = setTimeout(async () => {
        this.SET_OVERLAY(true);
        try {
          // Add small delay before fetching the API
          await new Promise((resolve) => setTimeout(resolve, 300));

          // Fetch both summary and items
          await Promise.all([this.fetchSummary(), this.fetchItem()]);
        } catch (error) {
          console.error("Error in debounced filter:", error);
        } finally {
          this.SET_OVERLAY(false);
        }
      }, 500); // 500ms debounce delay
    },

    // Handle batch print from child component
    handleBatchPrint() {
      this.executeBatchPrint();
    },

    // Handle Excel export from child component
    async handleExcelExport() {
      this.SET_OVERLAY(true);
      try {
        // Build the same URL parameters as fetchItem for consistency
        let url = `download_bill_report?status=${this.status.value}&start_date=${this.start_date}&end_date=${this.end_date}&q=${this.search}&locale=${this.$i18n.locale}`;

        // Add all filter parameters
        if (
          this.logistics_id &&
          this.logistics_id !== "all" &&
          this.logistics_id !== null &&
          this.logistics_id !== undefined
        ) {
          url += `&logistics_id=${this.logistics_id}`;
        }
        if (
          this.cashier_id &&
          this.cashier_id !== "all" &&
          this.cashier_id !== null &&
          this.cashier_id !== undefined
        ) {
          url += `&cashier_id=${this.cashier_id}`;
        }
        if (
          this.customer_id &&
          this.customer_id !== "all" &&
          this.customer_id !== null &&
          this.customer_id !== undefined
        ) {
          url += `&customer_id=${this.customer_id}`;
        }
        if (this.start_time) {
          url += `&start_time=${this.start_time}`;
        }
        if (this.end_time) {
          url += `&end_time=${this.end_time}`;
        }
        if (this.verification_status && this.verification_status !== "all") {
          url += `&verification_status=${this.verification_status}`;
        }
        const paymentMethodParam = this.paymentMethodForApi();
        if (paymentMethodParam && paymentMethodParam !== "all") {
          url += `&payment_method=${paymentMethodParam}`;
        }
        // Download Excel file with proper blob handling
        const data = await this.$http.get(url, {
          responseType: "blob",
        });

        const blobUrl = window.URL.createObjectURL(new Blob([data.data]));
        const link = document.createElement("a");
        const fileName = `bill_report_${this.start_date}_to_${this.end_date}.xlsx`;

        link.href = blobUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL
        window.URL.revokeObjectURL(blobUrl);

        toast(this, "excel_export_success");
      } catch (err) {
        console.error("Error exporting Excel:", err);
        error(this, "excel_export_failed");
      } finally {
        this.SET_OVERLAY(false);
      }
    },

    // Get advanced filter state from localStorage
    getAdvancedFilterState() {
      try {
        const saved = localStorage.getItem("billReport_showAdvancedFilter");
        return saved ? JSON.parse(saved) : false;
      } catch (error) {
        console.warn(
          "Error loading advanced filter state from localStorage:",
          error,
        );
        return false;
      }
    },

    // Save advanced filter state to localStorage
    saveAdvancedFilterState() {
      try {
        // Only save if advanced reports are enabled
        if (this.isAdvancedReportEnabled) {
          localStorage.setItem(
            "billReport_showAdvancedFilter",
            JSON.stringify(this.showAdvancedFilter),
          );
        }
      } catch (error) {
        console.warn(
          "Error saving advanced filter state to localStorage:",
          error,
        );
      }
    },

    // Format summary value based on permissions
    formatSummaryValue(item) {
      // Check if this is a cost or profit summary that requires permission
      if (item.title === "total_cost") {
        if (!hasPermission("show_cost")) {
          return "N/A";
        }
      } else if (item.title === "total_profit") {
        if (!hasPermission("show_profit")) {
          return "N/A";
        }
      }

      // For other summaries or if permission exists, format the value
      return this.$options.filters.NumberFormat(item.total);
    },

    // Check if user has a specific permission
    checkPermission(permissionCode) {
      return hasPermission(permissionCode);
    },
    formatCost(value) {
      if (this.checkPermission("show_cost")) {
        return this.$options.filters.NumberFormat(value);
      }
      return "N/A";
    },
    formatProfit(value) {
      if (this.checkPermission("show_profit")) {
        return this.$options.filters.NumberFormat(value);
      }
      return "N/A";
    },
    marginNote(item) {
      if (!this.checkPermission("show_profit") || !item.grand_total) return "";
      const margin = (item.total_profit / item.grand_total) * 100;
      return `${margin.toFixed(2)}%`;
    },
    initials(name) {
      return (name || "").trim().slice(0, 2).toUpperCase();
    },
    tagClass(status) {
      const color = this.orderStatusColor(status);
      if (color === "green" || color === "success") return "br-tag--success";
      if (color === "red" || color === "error") return "br-tag--error";
      if (color === "orange" || color === "warning") return "br-tag--warning";
      return "br-tag--neutral";
    },
    verifyTagClass(status) {
      if (status === "approved") return "br-tag--success";
      if (status === "rejected") return "br-tag--error";
      return "br-tag--warning";
    },

    // Status tab select
    selectTab(t) {
      this.activeStatusTab = t.value;
      if (t.value === "cancelled_items") {
        this.status = { text: t.text, value: "all" };
        this.hasCancelledItems = true;
      } else {
        this.hasCancelledItems = false;
        this.status = { text: t.text, value: t.value };
      }
    },

    statusCount(value) {
      if (!this.billReportsData.length) return 0;
      if (value === "all") {
        return this.activeStatusTab === "all"
          ? (this.billReports.total || this.billReportsData.length)
          : this.billReportsData.length;
      }
      if (value === "cancelled_items") {
        if (this.activeStatusTab === "cancelled_items") {
          return this.billReports.total || this.billReportsData.length;
        }
        return this.billReportsData.filter((item) => item.cancel_summary != null).length;
      }
      return this.billReportsData.filter((item) => item.status === value).length;
    },

    // Column sort
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = -this.sortDir;
      } else {
        this.sortKey = key;
        this.sortDir = 1;
      }
    },

    // Row selection
    isSelected(item) {
      return this.selectedTickets.some((t) => t.id === item.id);
    },
    toggleSelect(item) {
      if (this.isSelected(item)) {
        this.selectedTickets = this.selectedTickets.filter(
          (t) => t.id !== item.id,
        );
      } else {
        this.selectedTickets = [...this.selectedTickets, item];
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        const ids = new Set(this.sortedBillData.map((r) => r.id));
        this.selectedTickets = this.selectedTickets.filter(
          (t) => !ids.has(t.id),
        );
      } else {
        const existingIds = new Set(this.selectedTickets.map((t) => t.id));
        const toAdd = this.sortedBillData.filter((r) => !existingIds.has(r.id));
        this.selectedTickets = [...this.selectedTickets, ...toAdd];
      }
    },

    // Pagination (mirrors the shared DataTable's server-side paging)
    onPerPageChange(value) {
      const next = { page: 1, itemsPerPage: Number(value) };
      const prev = {
        page: this.billReports.current_page,
        itemsPerPage: this.per_page,
      };
      this.SET_PER_PAGE(Number(value));
      const url = Help.paginate(this.billReports, next, prev);
      this.fetch({ url, commit: "SET_BILL_REPORT", that: this });
    },
    goToPage(delta) {
      const current = this.billReports.current_page;
      const last = this.billReports.last_page;
      const nextPage = current + delta;
      if (nextPage < 1 || nextPage > last) return;
      const next = { page: nextPage, itemsPerPage: this.per_page };
      const prev = { page: current, itemsPerPage: this.per_page };
      const url = Help.paginate(this.billReports, next, prev);
      this.fetch({ url, commit: "SET_BILL_REPORT", that: this });
    },
  },
};
</script>

<style scoped>
.br-page {
  --br-accent: var(--v-primary-base, #5b39c9);
  --br-accent-soft: rgba(91, 57, 201, 0.12);
  --br-accent-softer: rgba(91, 57, 201, 0.07);
  --br-accent-border: rgba(91, 57, 201, 0.35);
  --br-border: #e2e8f0;
  --br-muted: #94a3b8;
  --br-text: #141021;
  --br-radius-lg: 16px;
  --br-radius-md: 10px;
  max-width: 100%;
  background: #f6f6fa;
}

.br-page--dark {
  --br-text: #ffffff;
  --br-border: rgba(255, 255, 255, 0.08);
  background: #1b1d30;
}

.br-page--dark .br-panel,
.br-page--dark .br-panel.v-card.v-sheet {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.br-page section {
  width: 100%;
}

/* ---- KPI cards ---- */

/* ---- Panel ---- */
.br-panel,
.br-panel.v-card.v-sheet {
  background: #ffffff !important;
  border: 1px solid var(--br-border) !important;
  border-radius: 14px !important;
  box-shadow: none !important;
}
.br-panel-header {
  gap: 10px;
}
.br-accent-bar {
  width: 3px;
  height: 15px;
  background: var(--br-accent);
  border-radius: 2px;
}
.br-panel-title {
  margin: 0;
  font-size: 17px;
  font-weight: 500;
  color: var(--br-text);
}
.br-panel-count {
  font-size: 11.5px;
  color: var(--br-muted);
}
.br-tabs {
  display: flex;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 10px;
  background: #f1f2f7;
  border: 1px solid var(--br-border);
}
.br-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 4px 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  color: var(--br-muted);
  transition: all 0.15s ease;
}
.br-tab--active {
  color: #5b39c9 !important;
  background: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  font-weight: 700;
}
.br-page--dark .br-tab--active {
  color: #c4b5fd !important;
  background: rgba(154, 140, 240, 0.18) !important;
  box-shadow: none;
}
.br-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 17px;
  height: 17px;
  margin-left: 4px;
  padding: 0 4px;
  border-radius: 999px;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  background: rgba(148, 163, 184, 0.14);
}
.br-tab--active .br-tab-count {
  background: rgba(91, 57, 201, 0.14);
  color: #5b39c9;
}
.br-page--dark .br-tab--active .br-tab-count {
  background: rgba(167, 139, 250, 0.25);
  color: #c4b5fd;
}

/* ---- Filter row ---- */
.br-filter-row {
  gap: 10px;
}
.br-search {
  flex: 1 1 220px;
  min-width: 210px;
  max-width: 300px;
}
.br-date-arrow {
  color: var(--br-muted);
}
.br-date-range ::v-deep .v-input__append-inner {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  align-self: center !important;
  color: #94a3b8 !important;
}
.br-date-range ::v-deep .v-input__append-inner .v-icon {
  color: #94a3b8 !important;
}
.br-select {
  max-width: 180px;
}
.br-select--verification {
  max-width: 205px;
}
.br-clear-link {
  font-size: 12px;
  color: var(--br-accent);
  cursor: pointer;
}
.br-export-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 13px;
  border-radius: var(--br-radius-md);
  border: 1px solid var(--br-border);
  color: var(--br-muted);
  font-size: 13px;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}
.br-export-btn:hover {
  color: var(--br-text);
  border-color: #cbd5e1;
}

/* ---- Bulk selection bar ---- */
.br-selection-bar {
  gap: 12px;
  padding: 9px 13px;
  border-radius: var(--br-radius-md);
  background: var(--br-accent-softer);
  border: 1px solid var(--br-accent-border);
}
.br-selection-label {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--br-accent);
}
.br-selection-total {
  font-size: 12.5px;
  color: var(--br-muted);
}
.br-selection-action {
  font-size: 12px;
  color: var(--br-accent);
  cursor: pointer;
  padding: 4px 10px;
  border: 1px solid var(--br-accent-border);
  border-radius: 999px;
  margin-left: 8px;
}

/* ---- Table ---- */
.br-table-scroll {
  overflow-x: auto;
}
.br-table-inner {
  min-width: 1180px;
}
.br-row {
  display: grid;
  grid-template-columns:
    34px 34px minmax(140px, 1.1fr) 68px minmax(160px, 1.2fr) 132px 88px 64px
    100px 108px 116px 108px 96px;
  gap: 10px;
  align-items: center;
}
.br-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.br-col-total_qty,
.br-col-total_price_buy,
.br-col-total_profit,
.br-col-grand_total,
.br-col-created_at,
.br-col-manage {
  justify-content: flex-end;
}
.br-head-row {
  padding: 0 6px 9px;
  border-bottom: 1px solid var(--br-border);
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--br-muted);
}
.br-cell--sortable {
  cursor: pointer;
  user-select: none;
}
.br-sort-caret {
  color: var(--br-accent);
}
.br-body-row {
  padding: 11px 6px;
  border-bottom: 1px solid #f1f2f7;
  cursor: pointer;
  transition: background 0.12s ease;
}
.br-body-row:hover {
  background: var(--br-accent-softer);
}
.br-row--selected {
  background: var(--br-accent-softer);
}
.br-cell-check {
  justify-content: flex-start;
}
.br-checkbox {
  width: 15px;
  height: 15px;
  flex: 0 0 15px;
  border-radius: 4px;
  border: 1px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.br-checkbox--on {
  background: var(--br-accent);
  border-color: var(--br-accent);
}
.br-stack {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.br-stack-end {
  align-items: flex-end;
}
.br-code {
  font-size: 13px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.br-col-code {
  justify-content: flex-start;
  text-align: left;
}
.br-cell.br-col-code.br-stack {
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
}
.br-col-code .br-stack {
  align-items: flex-start;
  text-align: left;
}
.br-subtext {
  font-size: 10.5px;
  color: var(--br-muted);
  font-variant-numeric: tabular-nums;
}
.br-seller {
  gap: 8px;
}
.br-avatar {
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10.5px;
  font-weight: 700;
  color: var(--br-accent);
  background: var(--br-accent-soft);
}
.br-seller-name {
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.br-customer {
  font-size: 10.5px;
  color: var(--br-muted);
}
.br-customer--empty {
  font-style: italic;
}
.br-tags {
  gap: 6px;
  flex-wrap: wrap;
}
.br-tag {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 999px;
  white-space: nowrap;
  border: 1px solid transparent;
}
.br-tag--success {
  color: #1b7a3d;
  background: #e3f7ea;
  border-color: #bdeccb;
}
.br-tag--error {
  color: #b3261e;
  background: #fdecea;
  border-color: #f6c6c2;
}
.br-tag--warning {
  color: #9a6700;
  background: #fff3d6;
  border-color: #f3d896;
}
.br-tag--neutral {
  color: var(--br-muted);
  background: #f1f2f7;
  border-color: var(--br-border);
}
.br-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  color: #475569;
}
.br-profit {
  color: var(--br-accent);
}
.br-total-strong {
  font-weight: 500;
  color: var(--br-text);
  font-size: 14px;
}
.br-actions {
  gap: 4px;
}
.br-action-icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: 1px solid var(--br-border);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--br-muted);
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}
.br-action-icon:hover {
  color: var(--br-accent);
  border-color: var(--br-accent-border);
}
.br-foot-row {
  padding: 11px 6px;
  border-top: 1px solid var(--br-border);
}
.br-foot-label {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--br-muted);
}
.br-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 44px 20px;
}
.br-empty-text {
  font-size: 13px;
  color: var(--br-muted);
}

/* ---- Pagination ---- */
.br-pagination {
  font-size: 12px;
  color: var(--br-muted);
}
.br-native-select {
  border: 1px solid var(--br-border);
  border-radius: 8px;
  padding: 5px 8px;
  font-size: 12.5px;
  color: var(--br-text);
  background: #ffffff;
}
.br-page-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}
.br-page-btn {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: 1px solid var(--br-border);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--br-muted);
  cursor: pointer;
}
.br-page-btn--disabled {
  opacity: 0.4;
  cursor: default;
  pointer-events: none;
}
.br-page-current {
  min-width: 26px;
  height: 26px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  color: var(--br-accent);
  background: var(--br-accent-soft);
  border: 1px solid var(--br-accent-border);
}

.br-page--dark .br-body-row {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}
.br-page--dark .br-checkbox:not(.br-checkbox--on) {
  border-color: rgba(255, 255, 255, 0.22);
}
.br-page--dark .br-num {
  color: #cbd5e1;
}
.br-page--dark .br-native-select {
  background: #262638;
  color: #f1f5f9;
  border-color: rgba(255, 255, 255, 0.12);
}
.br-page--dark .br-native-select option {
  background: #1e1e2d;
  color: #f1f5f9;
}
.br-page--dark .br-page-btn {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.12);
  color: #cbd5e1;
}
.br-page--dark .br-page-current {
  color: #c4b5fd;
  background: rgba(154, 140, 240, 0.18);
  border-color: rgba(154, 140, 240, 0.4);
  font-weight: 700;
}

@media (max-width: 960px) {
  .br-page {
    padding: 12px !important;
  }

  .br-panel .v-card__text {
    padding: 14px !important;
  }

  .br-panel-header {
    align-items: flex-start !important;
  }

  .br-tabs {
    width: 100%;
    overflow-x: auto;
  }

  .br-tab {
    flex: 1 0 auto;
    text-align: center;
  }

  .br-filter-row {
    align-items: stretch !important;
  }

  .br-search,
  .br-select,
  .br-select--verification {
    flex: 1 1 210px;
    max-width: none;
  }

  .br-date-range {
    flex: 1 1 100%;
  }

  .br-date-range .ps-date-picker {
    flex: 1 1 0;
    max-width: none !important;
  }

  .br-export-btn {
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .br-selection-bar {
    align-items: flex-start !important;
    flex-wrap: wrap;
  }

  .br-selection-bar .v-spacer {
    display: none;
  }

  .br-pagination {
    gap: 8px;
  }

  .br-pagination .v-spacer {
    display: none;
  }
}

/* Batch print styles */
.batch-print-wrapper {
  display: none;
}

@media print {
  .batch-print-wrapper {
    display: block !important;
  }

  .batch-bill-page {
    page-break-after: always !important;
    break-after: always !important;
    -webkit-break-after: always !important;
    min-height: 100vh !important;
    display: block !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .batch-bill-page.last-page {
    page-break-after: auto !important;
    break-after: auto !important;
    -webkit-break-after: auto !important;
  }

  .batch-bill-page:not(.first-page) {
    page-break-before: always !important;
    break-before: always !important;
    -webkit-break-before: always !important;
  }

  .receipt-wrapper {
    break-inside: avoid !important;
    -webkit-column-break-inside: avoid !important;
    page-break-inside: avoid !important;
    display: block !important;
    width: 100% !important;
  }
}
</style>
